import { AbstractMesh, Scene, TransformNode, Vector3 } from '@babylonjs/core';

const GROUND_CLEARANCE = 0.02;

export type AnchorCategory = 'floor' | 'wall' | 'center' | 'ceiling';

export interface FloorBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
}

export interface PlacementContext {
  floorBoundsMap?: Map<string, FloorBounds> | null;
  floorId?: string | null;
  scaleMultiplier?: number;
}

/**
 * Anchor-based asset placement utility.
 * Ported from illum-building-visualization viewer-app/public/services/assets/AssetPlacement.js
 *
 * Handles:
 *  - Finding anchor nodes in the Babylon scene by ID (ANCHOR_F001_R06_WALL_001 etc.)
 *  - Categorising anchors (floor / wall / center / ceiling / beacon)
 *  - Resolving floor and room for an asset from anchor data, parent hierarchy, or Y-position inference
 *  - Scaling assets to a fraction of floor height based on category
 *  - Snapping asset bottoms to the floor surface
 *  - Clamping Y within the floor slab so assets cannot float above the building
 */
export class AssetPlacementUtil {
  private scene: Scene;
  private _floorHeight = 3;

  constructor(scene: Scene) {
    this.scene = scene;
  }

  /** glTF/GLB nodes are often `Parent/Child/ANCHOR_F001_R06_WALL_001`; placements use the tail id only. */
  private nodeNameTailUpper(name: string | null | undefined): string {
    if (name == null || name === '') return '';
    const parts = String(name).replace(/\\/g, '/').split('/');
    return parts[parts.length - 1].toUpperCase();
  }

  findAnchorNode(anchorId: string): AbstractMesh | TransformNode | null {
    if (!anchorId) return null;
    const up = anchorId.toUpperCase();
    const marker = `${anchorId}_marker`;
    const mkr = marker.toUpperCase();

    let node =
      (this.scene.getNodeByName(anchorId) as AbstractMesh | TransformNode | null) ||
      this.scene.getTransformNodeByName(anchorId) ||
      this.scene.getMeshByName(anchorId);
    if (node) return node;

    const markerMesh = this.scene.getMeshByName(marker);
    if (markerMesh) {
      const p = markerMesh.parent;
      if (p && this.nodeNameTailUpper(p.name) === up) return p as TransformNode;
      return markerMesh;
    }

    for (const tn of this.scene.transformNodes) {
      if (this.nodeNameTailUpper(tn.name) === up) return tn;
    }
    for (const m of this.scene.meshes) {
      const tail = this.nodeNameTailUpper(m.name);
      if (tail === mkr) {
        const p = m.parent;
        if (p && this.nodeNameTailUpper(p.name) === up) return p as TransformNode;
        return m;
      }
      if (tail === up) return m;
    }
    return null;
  }

  categoryFromAnchorId(anchorId: string): AnchorCategory {
    const upper = (anchorId || '').toUpperCase();
    if (upper.includes('BEACON')) return 'floor';
    if (upper.includes('_WALL_')) return 'wall';
    if (upper.includes('_CENTER_')) return 'center';
    if (upper.includes('_CEILING_')) return 'ceiling';
    return 'floor';
  }

  floorFromAnchorId(anchorId: string): string {
    const parts = (anchorId || '').split('_');
    if (parts.length > 1 && parts[1].startsWith('F')) {
      return parts[1];
    }
    return '1';
  }

  floorFromParentNodes(anchorNode: AbstractMesh | TransformNode | null): string | null {
    let n = anchorNode?.parent as any;
    while (n) {
      const name = n.name || '';
      if (name.startsWith('Floor_')) {
        return name.replace(/^Floor_/, '');
      }
      n = n.parent;
    }
    return null;
  }

  inferFloorFromBounds(anchorNode: AbstractMesh | TransformNode, floorBoundsMap: Map<string, FloorBounds>): string | null {
    if (!anchorNode || !floorBoundsMap?.size) return null;
    anchorNode.computeWorldMatrix(true);
    const y = anchorNode.getAbsolutePosition().y;
    const eps = 0.25;
    let insideId: string | null = null;
    let insideDist = Infinity;
    for (const [fid, b] of floorBoundsMap) {
      if (y >= b.minY - eps && y <= b.maxY + eps) {
        const dist = Math.abs(y - b.minY);
        if (dist < insideDist) {
          insideDist = dist;
          insideId = fid;
        }
      }
    }
    if (insideId != null) return insideId;

    let nearId: string | null = null;
    let nearDist = Infinity;
    for (const [fid, b] of floorBoundsMap) {
      const d = y < b.minY ? b.minY - y : y > b.maxY ? y - b.maxY : 0;
      if (d < nearDist) {
        nearDist = d;
        nearId = fid;
      }
    }
    return nearId;
  }

  roomFromAnchorId(anchorId: string): string | null {
    const s = anchorId || '';
    const m = s.match(/_R(\d+)/i);
    if (m) return m[1];
    const parts = s.split('_');
    for (const p of parts) {
      if (/^R\d+$/i.test(p)) return p.replace(/^R/i, '');
    }
    return null;
  }

  resolveFloorForAsset(
    anchorNode: AbstractMesh | TransformNode,
    anchorId: string,
    itemFloor: string | null | undefined,
    floorBoundsMap: Map<string, FloorBounds> | null
  ): string {
    if (itemFloor != null && String(itemFloor).trim() !== '') {
      return String(itemFloor);
    }
    const fromParent = this.floorFromParentNodes(anchorNode);
    if (fromParent != null && String(fromParent).trim() !== '') {
      return String(fromParent);
    }
    const fromAnchor = this.floorFromAnchorId(anchorId);
    const multiFloor = floorBoundsMap && floorBoundsMap.size > 1;
    if (multiFloor && fromAnchor === '1') {
      const fromY = this.inferFloorFromBounds(anchorNode, floorBoundsMap!);
      if (fromY != null && String(fromY).trim() !== '') {
        return String(fromY);
      }
    }
    return fromAnchor;
  }

  resolveRoomForAsset(item: { room?: string | null }, anchorId: string): string | null {
    if (item?.room != null && String(item.room).trim() !== '') {
      return String(item.room);
    }
    return this.roomFromAnchorId(anchorId);
  }

  calibrate(floorBoundsMap: Map<string, FloorBounds> | null): void {
    if (!floorBoundsMap || floorBoundsMap.size === 0) return;
    let totalH = 0, cnt = 0;
    for (const b of floorBoundsMap.values()) {
      const h = b.maxY - b.minY;
      if (h > 0) { totalH += h; cnt++; }
    }
    if (cnt > 0) this._floorHeight = totalH / cnt;
  }

  get floorHeight(): number {
    return this._floorHeight;
  }

  private targetFraction(category: AnchorCategory): number {
    switch (category) {
      case 'center':  return 0.20;
      case 'wall':    return 0.25;
      case 'ceiling': return 0.10;
      default: return 0.30;
    }
  }

  private normalizeFloorKey(f: string | null | undefined): string {
    if (f == null || f === '') return '';
    const s = String(f).trim();
    if (s === '') return '';
    const u = s.toUpperCase();
    const tail = u.startsWith('F') ? u.slice(1) : u;
    if (/^\d+$/.test(tail)) {
      const n = parseInt(tail, 10);
      if (!Number.isNaN(n)) return String(n);
    }
    return u;
  }

  private boundsForFloorId(floorBoundsMap: Map<string, FloorBounds> | null, floorIdRaw: string | null): FloorBounds | null {
    if (!floorBoundsMap?.size || floorIdRaw == null) return null;
    const want = this.normalizeFloorKey(floorIdRaw);
    for (const [k, b] of floorBoundsMap) {
      if (this.normalizeFloorKey(k) === want) return b;
    }
    return null;
  }

  walkSurfaceY(floorBoundsMap: Map<string, FloorBounds> | null, floorIdRaw: string, fallbackY: number): number {
    const b = this.boundsForFloorId(floorBoundsMap, floorIdRaw);
    if (b && Number.isFinite(b.minY)) return b.minY + GROUND_CLEARANCE;
    return fallbackY;
  }

  private hierarchyWorldMinY(meshRoot: AbstractMesh | TransformNode): number | null {
    meshRoot.computeWorldMatrix(true);
    const meshes = meshRoot.getChildMeshes ? meshRoot.getChildMeshes(false) : [];
    let minY = Infinity;
    for (const m of meshes) {
      if (!m.getBoundingInfo) continue;
      m.computeWorldMatrix(true);
      const bb = m.getBoundingInfo()?.boundingBox;
      if (!bb) continue;
      const y = bb.minimumWorld.y;
      if (y < minY) minY = y;
    }
    if ((meshRoot as AbstractMesh).getBoundingInfo && typeof (meshRoot as AbstractMesh).getBoundingInfo === 'function') {
      meshRoot.computeWorldMatrix(true);
      const bb = (meshRoot as AbstractMesh).getBoundingInfo()?.boundingBox;
      if (bb) {
        const y = bb.minimumWorld.y;
        if (y < minY) minY = y;
      }
    }
    return Number.isFinite(minY) ? minY : null;
  }

  private snapBottomToWorldY(meshRoot: AbstractMesh | TransformNode, targetY: number): void {
    const minY = this.hierarchyWorldMinY(meshRoot);
    if (minY == null) return;
    meshRoot.position.y += targetY - minY;
    meshRoot.computeWorldMatrix(true);
  }

  private measureHeight(meshRoot: AbstractMesh | TransformNode): number {
    const children = meshRoot.getChildMeshes ? meshRoot.getChildMeshes(false) : [];
    if (children.length === 0) return 1;

    meshRoot.computeWorldMatrix(true);
    let minY = Infinity, maxY = -Infinity;
    let minX = Infinity, maxX = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    for (const m of children) {
      m.computeWorldMatrix(true);
      const bb = m.getBoundingInfo();
      if (!bb || !bb.boundingBox) continue;
      const bmin = bb.boundingBox.minimumWorld;
      const bmax = bb.boundingBox.maximumWorld;
      if (bmin.y < minY) minY = bmin.y;
      if (bmax.y > maxY) maxY = bmax.y;
      if (bmin.x < minX) minX = bmin.x;
      if (bmax.x > maxX) maxX = bmax.x;
      if (bmin.z < minZ) minZ = bmin.z;
      if (bmax.z > maxZ) maxZ = bmax.z;
    }

    if (minY === Infinity || maxY === -Infinity) return 1;
    const h = maxY - minY;
    const w = maxX - minX;
    const d = maxZ - minZ;
    const largest = Math.max(h, w, d);
    return largest > 0.001 ? largest : 1;
  }

  /**
   * Place a mesh at an anchor node with category-based Y positioning, scale-to-floor-height,
   * and bottom snapping. Returns true on success.
   */
  place(
    meshRoot: AbstractMesh | TransformNode,
    anchorNode: AbstractMesh | TransformNode,
    category: AnchorCategory,
    ctx: PlacementContext = {}
  ): boolean {
    if (!meshRoot || !anchorNode) return false;

    const cat = category || 'floor';
    const pos = anchorNode.getAbsolutePosition().clone();
    const bounds = ctx.floorBoundsMap && ctx.floorId != null
      ? this.boundsForFloorId(ctx.floorBoundsMap, ctx.floorId)
      : null;

    const groundCategories = cat === 'center' || cat === 'floor';
    let targetGroundY = pos.y;
    if (groundCategories && bounds && Number.isFinite(bounds.minY)) {
      targetGroundY = bounds.minY + GROUND_CLEARANCE;
    }

    // Clamp Y within the floor slab so assets cannot sit above the building stack
    if (bounds && Number.isFinite(bounds.minY) && Number.isFinite(bounds.maxY)) {
      const slabH = Math.max(0.08, bounds.maxY - bounds.minY);
      const margin = Math.min(0.25, slabH * 0.08);
      const lo = groundCategories ? bounds.minY + GROUND_CLEARANCE : bounds.minY + margin;
      const hi = bounds.maxY - margin;
      if (hi >= lo) {
        targetGroundY = Math.min(hi, Math.max(lo, targetGroundY));
      }
    }

    // Reset transform and measure
    meshRoot.position.set(0, 0, 0);
    meshRoot.scaling = new Vector3(1, 1, 1);

    const assetSize = this.measureHeight(meshRoot);
    const targetSize = this._floorHeight * this.targetFraction(cat);
    const mult =
      ctx.scaleMultiplier != null && Number.isFinite(ctx.scaleMultiplier)
        ? Math.max(0.05, ctx.scaleMultiplier)
        : 1;
    const s = (targetSize / assetSize) * mult;
    meshRoot.scaling = new Vector3(s, s, s);

    meshRoot.computeWorldMatrix(true);
    const toRefresh = meshRoot.getChildMeshes ? meshRoot.getChildMeshes(false) : [];
    for (const m of toRefresh) {
      if ((m as AbstractMesh).refreshBoundingInfo) (m as AbstractMesh).refreshBoundingInfo(true, false);
    }
    if ((meshRoot as AbstractMesh).refreshBoundingInfo) {
      (meshRoot as AbstractMesh).refreshBoundingInfo(true, false);
    }

    // Copy rotation from anchor
    const anchorAny = anchorNode as any;
    if (anchorAny.rotationQuaternion) {
      (meshRoot as any).rotationQuaternion = anchorAny.rotationQuaternion.clone();
    } else if (anchorAny.rotation) {
      (meshRoot as any).rotation.copyFrom(anchorAny.rotation);
    }

    // Snap bottom to floor surface for ground-level assets
    if (groundCategories) {
      this.snapBottomToWorldY(meshRoot, targetGroundY);
    }

    return true;
  }

  /**
   * Build a FloorBounds map from the floorMappingTable entries.
   */
  static buildFloorBoundsMap(
    floorMapping: Map<string, { bounds?: { min: Vector3; max: Vector3 } }>
  ): Map<string, FloorBounds> {
    const map = new Map<string, FloorBounds>();
    floorMapping.forEach((data, floorId) => {
      if (data.bounds) {
        map.set(floorId, {
          minX: data.bounds.min.x,
          maxX: data.bounds.max.x,
          minY: data.bounds.min.y,
          maxY: data.bounds.max.y,
          minZ: data.bounds.min.z,
          maxZ: data.bounds.max.z,
        });
      }
    });
    return map;
  }
}
